import React, { useEffect, useState } from "react";
import {
  ChevronRight, Check, ArrowLeft, Zap, User, Tv2,
  Calendar, IndianRupee, Banknote, Tag, Loader2, FileText,
  TrendingDown, TrendingUp, Plus, Receipt, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const fmt = (v) => `₹${Number(v || 0).toFixed(2)}`;

const STEPS = ["Customer", "Plan", "Confirm", "Done"];

function StepBar({ step }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${i < step
                  ? "bg-green-500 text-white shadow-sm"
                  : i === step
                    ? "bg-brand-primary text-white shadow-md ring-4 ring-brand-primary/20"
                    : "bg-slate-100 text-slate-400"
                }`}
            >
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span
              className={`text-[10px] mt-1 font-semibold ${i === step ? "text-brand-primary" : "text-slate-400"
                }`}
            >
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div
              className={`h-0.5 w-10 mb-4 rounded ${i < step ? "bg-green-400" : "bg-slate-200"
                }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─── Plan Card (selectable) ─────────────────────────────────────────────── */
function PlanCard({ p, selected, onSelect }) {
  const isSelected = selected?.id === p.id;
  return (
    <button
      onClick={() => onSelect(p)}
      className={`w-full text-left rounded-xl border-2 p-4 transition-all ${isSelected
          ? "border-brand-primary bg-brand-primary/5 shadow-md"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
        }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-brand-primary uppercase tracking-wide">
              {p.operator}
            </span>
            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium">
              {p.category}
            </span>
          </div>
          <p className="font-semibold text-brand-nav mt-1 truncate">{p.name}</p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Calendar className="w-3 h-3" /> {p.validity_days}d
            </span>
            {p.channel_count > 0 && (
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Tv2 className="w-3 h-3" /> {p.channel_count} ch
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="font-heading font-black text-2xl text-brand-accent">{fmt(p.base_cost)}</p>
          <p className="text-xs text-slate-400">MRP</p>
          {p.recharge_amount > 0 && p.recharge_amount !== p.base_cost && (
            <p className="text-xs text-green-600 font-semibold mt-1">
              Recharge: {fmt(p.recharge_amount)}
            </p>
          )}
          {p.dealer_price > 0 && (
            <p className="text-xs text-indigo-600 font-semibold">
              Dealer: {fmt(p.dealer_price)}
            </p>
          )}
        </div>
      </div>
      {p.description && (
        <p className="text-xs text-slate-400 mt-2 line-clamp-1">{p.description}</p>
      )}
    </button>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function AdminRecharge() {
  const [step, setStep] = useState(0);
  const [customers, setCustomers] = useState([]);   // [{customer, connections}]
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Selections
  const [selectedCustomer, setSelectedCustomer] = useState(null);  // {customer, connections}
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [extraDiscount, setExtraDiscount] = useState("");
  const [addonCharge, setAddonCharge] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  // Filters
  const [planSearch, setPlanSearch] = useState("");
  const [planCategory, setPlanCategory] = useState("All");
  const [customerSearch, setCustomerSearch] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/admin/recharge/customers"),
      api.get("/admin/products"),
    ])
      .then(([c, p]) => {
        setCustomers(c.data);
        setCatalog(p.data.filter((x) => x.is_active));
      })
      .catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  const go = (n) => setStep(n);
  const back = () => setStep((s) => s - 1);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    if (c.connections.length === 1) {
      setSelectedConnection(c.connections[0]);
      go(1);
    } else {
      setSelectedConnection(null);
      go(1);
    }
  };

  const finalAmount = () => {
    if (!selectedPlan) return 0;
    const base = selectedPlan.base_cost;
    const maxDisc = selectedPlan.discount || 0;
    const disc = Math.min(parseFloat(extraDiscount) || 0, maxDisc);
    const addon = parseFloat(addonCharge) || 0;
    return Math.max(base - disc + addon, 0);
  };

  const handleRecharge = async () => {
    if (!selectedConnection || !selectedPlan) return;
    setShowConfirmModal(false);
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/recharge", {
        customer_id_fk: selectedConnection.id,
        product_id: selectedPlan.id,
        extra_discount: parseFloat(extraDiscount) || 0,
        addon_charge: parseFloat(addonCharge) || 0,
        payment_note: paymentNote,
      });
      if (data.ok && data.status === 'success') {
        setResult(data);
        go(3);
        toast.success("Recharge processed successfully!");
      } else if (data.status === 'recharge_failed') {
        setResult({ ...data, partial: true });
        go(3);
        toast.error(data.message || "DishTV recharge failed");
      } else {
        toast.error(data.message || "Recharge failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recharge failed");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setSelectedCustomer(null);
    setSelectedConnection(null);
    setSelectedPlan(null);
    setExtraDiscount("");
    setAddonCharge("");
    setPaymentNote("");
    setResult(null);
  };

  // First, restrict catalog to the selected connection's group (if any)
  const groupFilteredCatalog = catalog.filter(p => {
    if (selectedConnection && selectedConnection.group_id) {
      return p.group_id === selectedConnection.group_id;
    }
    return true;
  });

  const categories = ["All", ...new Set(groupFilteredCatalog.map((p) => p.category))];

  const filteredCatalog = groupFilteredCatalog.filter((p) => {
    const q = planSearch.toLowerCase();
    return (
      (planCategory === "All" || p.category === planCategory) &&
      (!q || p.name.toLowerCase().includes(q) || p.operator.toLowerCase().includes(q))
    );
  });
  const filteredCustomers = customers.filter((c) => {
    const q = customerSearch.toLowerCase();
    return (
      !q ||
      c.customer?.name?.toLowerCase().includes(q) ||
      c.connections?.some((ci) => ci.dth_id?.toLowerCase().includes(q))
    );
  });

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h3 className="font-heading font-bold text-2xl text-brand-nav">Recharge Customer</h3>
        <p className="text-sm text-slate-400 mt-0.5">Cash collection — no payment gateway required.</p>
      </div>

      <StepBar step={step} />

      {/* ── Step 0: Select Customer ── */}
      {step === 0 && (
        <div>
          <h4 className="font-semibold text-brand-nav mb-3">Select Customer</h4>
          <input
            type="text"
            placeholder="Search by name or DTH ID..."
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            className="w-full mb-3 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          />
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {filteredCustomers.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-8">No active customers found.</p>
            )}
            {filteredCustomers.map((c, i) => (
              <button
                key={i}
                onClick={() => selectCustomer(c)}
                className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-brand-primary hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-brand-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-brand-nav">{c.customer?.name}</p>
                      <p className="text-xs text-slate-400">{c.customer?.mobile}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">
                      {c.connections?.length} connection{c.connections?.length !== 1 ? "s" : ""}
                    </p>
                    <p className="text-xs font-mono text-slate-400">
                      {c.connections?.[0]?.dth_id}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Step 1: Select DTH ID + Plan ── */}
      {step === 1 && selectedCustomer && (
        <div>
          <button onClick={back} className="flex items-center gap-1 text-sm text-brand-primary font-semibold mb-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {/* Customer summary */}
          <div className="bg-slate-50 rounded-xl p-4 mb-5 flex items-center gap-3 border border-slate-100">
            <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
              <User className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
              <p className="font-semibold text-brand-nav">{selectedCustomer.customer?.name}</p>
              <p className="text-xs text-slate-400">{selectedCustomer.customer?.mobile}</p>
            </div>
          </div>

          {/* DTH Connection selector */}
          {selectedCustomer.connections.length > 1 && (
            <div className="mb-5">
              <label className="block text-sm font-semibold text-slate-600 mb-2">Select DTH Connection</label>
              <div className="space-y-2">
                {selectedCustomer.connections.map((ci) => (
                  <button
                    key={ci.id}
                    onClick={() => setSelectedConnection(ci)}
                    className={`w-full text-left border-2 rounded-xl px-4 py-3 transition-all ${selectedConnection?.id === ci.id
                        ? "border-brand-primary bg-brand-primary/5"
                        : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                  >
                    <p className="font-semibold text-brand-nav">{ci.operator}</p>
                    <p className="text-xs font-mono text-slate-400">ID: {ci.dth_id}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          {selectedCustomer.connections.length === 1 && (
            <div className="mb-5 bg-white border border-slate-200 rounded-xl px-4 py-3">
              <p className="text-xs text-slate-400 mb-0.5">DTH Connection</p>
              <p className="font-semibold text-brand-nav">{selectedConnection?.operator}</p>
              <p className="text-xs font-mono text-slate-400">ID: {selectedConnection?.dth_id}</p>
            </div>
          )}

          {/* Plan selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">Choose Plan</label>
            <div className="flex gap-2 mb-3 flex-wrap">
              <input
                type="text"
                placeholder="Search plans..."
                value={planSearch}
                onChange={(e) => setPlanSearch(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 flex-1 min-w-0"
              />
              <select
                value={planCategory}
                onChange={(e) => setPlanCategory(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              >
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {filteredCatalog.map((p) => (
                <PlanCard
                  key={p.id}
                  p={p}
                  selected={selectedPlan}
                  onSelect={(plan) => {
                    setSelectedPlan(plan);
                    if (selectedConnection) go(2);
                  }}
                />
              ))}
              {filteredCatalog.length === 0 && (
                <p className="text-slate-400 text-sm text-center py-8">No active plans found.</p>
              )}
            </div>
          </div>

          {selectedConnection && selectedPlan && (
            <button
              onClick={() => go(2)}
              className="mt-4 w-full bg-brand-primary text-white font-semibold py-3 rounded-xl hover:bg-brand-primaryHover transition"
            >
              Continue to Confirm →
            </button>
          )}
        </div>
      )}

      {/* ── Step 2: Confirm ── */}
      {step === 2 && selectedConnection && selectedPlan && (
        <div>
          <button onClick={back} className="flex items-center gap-1 text-sm text-brand-primary font-semibold mb-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h4 className="font-semibold text-brand-nav mb-4">Confirm Recharge Details</h4>

          {/* Summary card */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto mb-4 shadow-sm">
            {/* Customer / connection */}
            <div className="px-5 py-4 bg-slate-50 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Customer</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-brand-nav">{selectedCustomer?.customer?.name}</p>
                  <p className="text-xs text-slate-400">{selectedCustomer?.customer?.mobile}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-brand-nav">{selectedConnection.operator}</p>
                  <p className="text-xs font-mono text-slate-400">ID: {selectedConnection.dth_id}</p>
                </div>
              </div>
            </div>

            {/* Plan */}
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Plan Details</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Plan</span>
                  <span className="font-semibold text-brand-nav">{selectedPlan.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Operator / Category</span>
                  <span className="text-slate-600">{selectedPlan.operator} · {selectedPlan.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Validity</span>
                  <span className="text-slate-600">{selectedPlan.validity_days} days</span>
                </div>
              </div>
            </div>

            {/* Pricing (admin sees all 3 amounts) */}
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Pricing</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1">
                    <Tag className="w-3 h-3" /> MRP (Customer Price)
                  </span>
                  <span className="font-semibold text-slate-700">{fmt(selectedPlan.base_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1">
                    <Zap className="w-3 h-3 text-purple-500" /> Recharge Amount
                    <span className="text-xs bg-purple-100 text-purple-600 px-1 rounded font-bold">API</span>
                  </span>
                  <span className="font-semibold text-purple-600">{fmt(selectedPlan.recharge_amount || selectedPlan.base_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 flex items-center gap-1">
                    <IndianRupee className="w-3 h-3 text-green-500" /> Dealer Price
                  </span>
                  <span className="font-semibold text-green-600">{fmt(selectedPlan.dealer_price || selectedPlan.base_cost)}</span>
                </div>
              </div>
            </div>

            {/* Discount input */}
            <div className="px-5 py-4 border-b border-slate-100">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                <TrendingDown className="w-3 h-3 inline mr-1 text-green-500" />
                Discount (Optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                <input
                  type="number"
                  min="0"
                  max={selectedPlan.discount || 0}
                  step="0.01"
                  placeholder="0.00"
                  value={extraDiscount}
                  onChange={(e) => setExtraDiscount(e.target.value)}
                  className="w-full pl-7 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400/40"
                />
              </div>
              <p className="text-xs text-green-600 mt-1">
                Max allowed: {fmt(selectedPlan.discount || 0)} (plan discount limit)
              </p>
            </div>

            {/* Add-on charge input */}
            <div className="px-5 py-4 border-b border-slate-100">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                <Plus className="w-3 h-3 inline mr-1 text-amber-500" />
                Add-on Charge (Optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={addonCharge}
                  onChange={(e) => setAddonCharge(e.target.value)}
                  className="w-full pl-7 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Service/convenience fee. Hidden in payment preview but included in total.
              </p>
            </div>

            {/* Payment note */}
            <div className="px-5 py-4 border-b border-slate-100">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                <FileText className="w-3 h-3 inline mr-1" /> Cash Memo / Note (optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Cash received, ref#123"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
            </div>

            {/* Final amount */}
            <div className="px-5 py-4 bg-gradient-to-r from-brand-primary/5 to-brand-accent/5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Amount to Collect (Cash)</p>
                  <p className="text-xs text-slate-400 mt-0.5">= MRP − Discount + Add-on</p>
                </div>
                <p className="font-heading font-black text-3xl text-brand-primary">{fmt(finalAmount())}</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-brand-primary to-brand-accent text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</>
            ) : (
              <><Banknote className="w-5 h-5" /> Confirm Cash Recharge</>
            )}
          </button>
          <p className="text-center text-xs text-slate-400 mt-2">
            Cash will be collected manually. No gateway payment.
          </p>
        </div>
      )}

      {/* ── Step 3: Done ── */}
      {step === 3 && result && (
        <div className="text-center space-y-5 py-8">
          {result?.partial ? (
            <>
              <div className="w-24 h-24 mx-auto rounded-full bg-amber-50 border-4 border-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-amber-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-brand-nav">Recharge Failed</h2>
                <p className="text-slate-500 mt-1">DishTV recharge could not be completed.</p>
                <p className="text-sm text-amber-600 mt-2 bg-amber-50 rounded-lg px-4 py-3 inline-block">
                  {result.message || "Check recharge logs for details."}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-left space-y-3 max-w-sm mx-auto">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Recharge ID</span>
                  <span className="font-mono text-xs">{result.recharge_id?.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Cash Collected</span>
                  <span className="font-semibold">{fmt(result.amount_charged)}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-24 h-24 mx-auto rounded-full bg-green-50 border-4 border-green-100 flex items-center justify-center">
                <Check className="w-12 h-12 text-green-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-brand-nav">Recharge Successful!</h2>
                <p className="text-slate-500 mt-1">The DTH subscription has been activated.</p>
                {result.dtv_message && (
                  <p className="text-xs text-green-600 mt-1">{result.dtv_message}</p>
                )}
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-left space-y-3 max-w-sm mx-auto">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Customer</span>
                  <span className="font-semibold">{selectedCustomer?.customer?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Plan</span>
                  <span className="font-semibold">{selectedPlan?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Plan active until</span>
                  <span className="font-semibold text-green-600">{result.plan_end}</span>
                </div>
                <hr className="border-slate-100" />
                <div className="flex justify-between">
                  <span className="text-slate-500 text-sm">Cash Collected</span>
                  <span className="font-heading font-bold text-xl text-brand-primary">
                    {fmt(result.amount_charged)}
                  </span>
                </div>
              </div>
            </>
          )}
          <button
            onClick={reset}
            className="mt-2 text-sm font-semibold text-brand-primary hover:underline"
          >
            <Receipt className="w-4 h-4 inline mr-1" />
            Recharge another →
          </button>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)" }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-brand-primary/10 px-5 py-4 border-b border-brand-primary/10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                <Banknote className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-brand-nav">Confirm Cash</h3>
                <p className="text-xs text-slate-500">Manual recharge processing</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600">
                Please confirm that you have collected the cash from the customer before proceeding.
              </p>
              <div className="bg-slate-50 rounded-lg p-3 flex justify-between items-center border border-slate-100">
                <span className="text-xs font-bold text-slate-500 uppercase">Amount to Collect</span>
                <span className="font-heading font-black text-xl text-brand-primary">{fmt(finalAmount())}</span>
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRecharge}
                className="flex-1 py-2.5 text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover rounded-xl transition shadow-md"
              >
                Yes, Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
